
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertTriangle,
  BarChart4,
  Calendar as CalendarIcon,
  ArrowLeft,
  BookOpen,
  PieChart,
  LineChart,
  BarChart,
  Activity,
  TrendingUp,
  ListFilter,
  Clock,
  Target,
  Loader2
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuthSession } from "@/hooks/useAuthSession";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, subMonths } from "date-fns";
import { cn } from "@/lib/utils";
import { 
  AreaChart, 
  Area, 
  LineChart as ReChartsLineChart,
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  BarChart as ReChartsBarChart,
  Bar,
  PieChart as ReChartsPieChart,
  Pie,
  Cell,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

// Types for the analytics data
interface QuizResult {
  id: string;
  user_id: string;
  session_id: string;
  score: number;
  total_questions: number;
  percentage: number;
  difficulty: string;
  date_filter?: string;
  time_period?: string;
  completed_at: string;
  questions?: any[];
  answers?: any[];
}

interface PerformanceTrend {
  date: string;
  percentage: number;
  difficulty: string;
}

interface TopicPerformance {
  topic: string;
  correct: number;
  total: number;
  percentage: number;
}

interface DifficultyDistribution {
  difficulty: string;
  count: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];
const DIFFICULTY_COLORS = {
  easy: '#4ade80',
  medium: '#facc15',
  hard: '#f87171'
};

const CurrentAffairsAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const { userId, isAuthenticated } = useAuthSession();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<QuizResult[]>([]);
  const [dateRange, setDateRange] = useState<Date | undefined>(subMonths(new Date(), 1));
  const [topicPerformance, setTopicPerformance] = useState<TopicPerformance[]>([]);
  const [performanceTrend, setPerformanceTrend] = useState<PerformanceTrend[]>([]);
  const [difficultyDistribution, setDifficultyDistribution] = useState<DifficultyDistribution[]>([]);
  const [timePerformance, setTimePerformance] = useState<any[]>([]);
  const [topicFilter, setTopicFilter] = useState<string>("all");
  const [difficultyFilter, setDifficultyFilter] = useState<string>("all");
  const [avgScore, setAvgScore] = useState<number>(0);
  const [totalQuizzes, setTotalQuizzes] = useState<number>(0);
  const [highestScore, setHighestScore] = useState<number>(0);
  const [weakestTopics, setWeakestTopics] = useState<TopicPerformance[]>([]);
  const [strongestTopics, setStrongestTopics] = useState<TopicPerformance[]>([]);

  // Fetch quiz results
  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setLoading(false);
      return;
    }

    const fetchResults = async () => {
      try {
        setLoading(true);
        const startDate = dateRange ? dateRange.toISOString() : subMonths(new Date(), 1).toISOString();

        const { data, error } = await supabase
          .from('current_affairs_quiz_results')
          .select('*')
          .eq('user_id', userId)
          .gte('completed_at', startDate)
          .order('completed_at', { ascending: true });

        if (error) {
          throw error;
        }

        if (data) {
          setResults(data as QuizResult[]);
          processData(data as QuizResult[]);
        }
      } catch (error) {
        console.error('Error fetching current affairs results:', error);
        toast.error('Failed to fetch analytics data');
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [userId, isAuthenticated, dateRange]);

  // Process the fetched data for visualizations
  const processData = (data: QuizResult[]) => {
    if (!data.length) return;

    // Calculate overall statistics
    setTotalQuizzes(data.length);
    const avgScoreValue = data.reduce((sum, item) => sum + item.percentage, 0) / data.length;
    setAvgScore(Math.round(avgScoreValue));
    const maxScore = Math.max(...data.map(item => item.percentage));
    setHighestScore(maxScore);

    // Process performance trends by date
    const trendData: PerformanceTrend[] = data.map(item => ({
      date: format(new Date(item.completed_at), 'MM/dd'),
      percentage: item.percentage,
      difficulty: item.difficulty
    }));
    setPerformanceTrend(trendData);

    // Process difficulty distribution
    const diffDistribution = data.reduce((acc, item) => {
      const difficulty = item.difficulty || 'unknown';
      const existingItem = acc.find(d => d.difficulty === difficulty);
      if (existingItem) {
        existingItem.count++;
      } else {
        acc.push({ difficulty, count: 1 });
      }
      return acc;
    }, [] as DifficultyDistribution[]);
    setDifficultyDistribution(diffDistribution);

    // Process topic performance
    const topicsMap = new Map<string, { correct: number, total: number }>();
    
    // Extract topics from questions and calculate performance
    data.forEach(result => {
      if (result.questions && result.answers) {
        result.questions.forEach((question, index) => {
          const answer = result.answers?.[index];
          const isCorrect = answer === question.correctanswer;
          
          // Extract topic from question title or content
          let topic = '';
          if (question.question) {
            // Get first few words as topic
            const words = question.question.split(' ');
            if (words.length > 3) {
              topic = words.slice(0, 3).join(' ') + '...';
            } else {
              topic = question.question;
            }
          }

          if (topic) {
            const topicData = topicsMap.get(topic) || { correct: 0, total: 0 };
            topicData.total++;
            if (isCorrect) {
              topicData.correct++;
            }
            topicsMap.set(topic, topicData);
          }
        });
      }
    });

    // Convert map to array
    const topicData: TopicPerformance[] = Array.from(topicsMap.entries()).map(([topic, data]) => ({
      topic,
      correct: data.correct,
      total: data.total,
      percentage: Math.round((data.correct / data.total) * 100)
    }));

    setTopicPerformance(topicData);

    // Set weakest and strongest topics
    const sortedTopics = [...topicData].sort((a, b) => a.percentage - b.percentage);
    setWeakestTopics(sortedTopics.slice(0, 3));
    setStrongestTopics(sortedTopics.reverse().slice(0, 3));

    // Process time-based performance
    // Use completed_at timestamps to calculate time between quizzes
    const timeData = data.map((item, index, arr) => {
      let hoursElapsed = 0;
      if (index > 0) {
        const currentTime = new Date(item.completed_at).getTime();
        const prevTime = new Date(arr[index - 1].completed_at).getTime();
        hoursElapsed = (currentTime - prevTime) / (1000 * 60 * 60);
      }
      
      return {
        date: format(new Date(item.completed_at), 'MM/dd'),
        score: item.percentage,
        hoursElapsed: Math.round(hoursElapsed * 10) / 10
      };
    });

    setTimePerformance(timeData);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate("/current-affairs")}
            className="mr-2"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Current Affairs
          </Button>
          <h1 className="text-2xl font-bold">Current Affairs Analytics</h1>
        </div>

        <div className="flex items-center space-x-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "justify-start text-left font-normal",
                  !dateRange && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateRange ? format(dateRange, "PPP") : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={dateRange}
                onSelect={setDateRange}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {!isAuthenticated ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-6">
            <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
            <h3 className="text-xl font-medium mb-2">Authentication Required</h3>
            <p className="text-muted-foreground text-center mb-4">
              Please log in to view your Current Affairs analytics.
            </p>
            <Button onClick={() => navigate("/auth")}>
              Sign In
            </Button>
          </CardContent>
        </Card>
      ) : results.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-6">
            <BookOpen className="h-12 w-12 text-primary mb-4" />
            <h3 className="text-xl font-medium mb-2">No Quiz Data</h3>
            <p className="text-muted-foreground text-center mb-4">
              Complete some Current Affairs quizzes to see your analytics.
            </p>
            <Button onClick={() => navigate("/current-affairs/quiz")}>
              Take a Quiz
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary statistics cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Average Score</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline">
                  <span className="text-2xl font-bold">{avgScore}%</span>
                  {avgScore > 70 ? (
                    <TrendingUp className="ml-2 h-4 w-4 text-green-500" />
                  ) : (
                    <TrendingUp className="ml-2 h-4 w-4 text-amber-500" />
                  )}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Quizzes Completed</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline">
                  <span className="text-2xl font-bold">{totalQuizzes}</span>
                  <span className="ml-2 text-sm text-muted-foreground">quizzes</span>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Highest Score</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline">
                  <span className="text-2xl font-bold">{highestScore}%</span>
                  <span className="ml-2 text-sm text-green-500">best performance</span>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Topics Covered</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline">
                  <span className="text-2xl font-bold">{topicPerformance.length}</span>
                  <span className="ml-2 text-sm text-muted-foreground">distinct topics</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="performance" className="w-full">
            <TabsList className="grid grid-cols-4 mb-6">
              <TabsTrigger value="performance" className="flex items-center gap-2">
                <LineChart className="h-4 w-4" />
                Performance Trends
              </TabsTrigger>
              <TabsTrigger value="topics" className="flex items-center gap-2">
                <PieChart className="h-4 w-4" />
                Topic Analysis
              </TabsTrigger>
              <TabsTrigger value="difficulty" className="flex items-center gap-2">
                <BarChart className="h-4 w-4" />
                Difficulty Analysis
              </TabsTrigger>
              <TabsTrigger value="time" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Time Impact
              </TabsTrigger>
            </TabsList>

            {/* Performance Trends Tab */}
            <TabsContent value="performance">
              <div className="grid grid-cols-1 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Performance Over Time</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="h-[350px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={performanceTrend}>
                          <defs>
                            <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="date" />
                          <YAxis domain={[0, 100]} />
                          <CartesianGrid strokeDasharray="3 3" />
                          <Tooltip />
                          <Area 
                            type="monotone" 
                            dataKey="percentage" 
                            stroke="#8884d8" 
                            fillOpacity={1} 
                            fill="url(#colorScore)" 
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Areas Needing Improvement</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {weakestTopics.length > 0 ? (
                        <div className="space-y-4">
                          {weakestTopics.map((topic, index) => (
                            <div key={index} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{topic.topic}</span>
                                <span className="text-sm text-red-500">{topic.percentage}%</span>
                              </div>
                              <div className="w-full bg-secondary rounded-full h-2.5">
                                <div 
                                  className="bg-red-500 h-2.5 rounded-full" 
                                  style={{ width: `${topic.percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground">No data available</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Your Strengths</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {strongestTopics.length > 0 ? (
                        <div className="space-y-4">
                          {strongestTopics.map((topic, index) => (
                            <div key={index} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{topic.topic}</span>
                                <span className="text-sm text-green-500">{topic.percentage}%</span>
                              </div>
                              <div className="w-full bg-secondary rounded-full h-2.5">
                                <div 
                                  className="bg-green-500 h-2.5 rounded-full" 
                                  style={{ width: `${topic.percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground">No data available</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* Topic Analysis Tab */}
            <TabsContent value="topics">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Topic Performance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[350px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={topicPerformance.slice(0, 5)}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey="total"
                            nameKey="topic"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          >
                            {topicPerformance.slice(0, 5).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name, props) => [`${value} questions`, props.payload.topic]} />
                          <Legend layout="vertical" verticalAlign="middle" align="right" />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Topic Mastery</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[350px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart 
                          cx="50%" 
                          cy="50%" 
                          outerRadius="80%" 
                          data={topicPerformance.slice(0, 8).map(topic => ({
                            ...topic,
                            fullMark: 100
                          }))}
                        >
                          <PolarGrid />
                          <PolarAngleAxis dataKey="topic" />
                          <PolarRadiusAxis angle={30} domain={[0, 100]} />
                          <Radar 
                            name="Percentage Correct" 
                            dataKey="percentage" 
                            stroke="#8884d8" 
                            fill="#8884d8" 
                            fillOpacity={0.6} 
                          />
                          <Legend />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card className="md:col-span-2">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Topic Performance Details</CardTitle>
                    <Select value={topicFilter} onValueChange={setTopicFilter}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Filter by topic" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Topics</SelectItem>
                        {topicPerformance.map((topic, index) => (
                          <SelectItem key={index} value={topic.topic}>
                            {topic.topic}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <ReChartsBarChart
                          data={topicPerformance.filter(
                            topic => topicFilter === "all" || topic.topic === topicFilter
                          )}
                          margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 100,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="topic"
                            angle={-45}
                            textAnchor="end"
                            height={70}
                            interval={0}
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis domain={[0, 100]} />
                          <Tooltip formatter={(value) => [`${value}%`]} />
                          <Legend />
                          <Bar dataKey="percentage" name="Percentage Correct" fill="#8884d8" />
                        </ReChartsBarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Difficulty Analysis Tab */}
            <TabsContent value="difficulty">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Difficulty Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[350px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={difficultyDistribution}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey="count"
                            nameKey="difficulty"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          >
                            {difficultyDistribution.map((entry) => (
                              <Cell 
                                key={`cell-${entry.difficulty}`} 
                                fill={DIFFICULTY_COLORS[entry.difficulty as 'easy' | 'medium' | 'hard'] || '#999'} 
                              />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [`${value} quizzes`]} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Performance by Difficulty</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[350px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <ReChartsBarChart
                          data={results.reduce((acc: any[], result) => {
                            const existingRecord = acc.find(r => r.difficulty === result.difficulty);
                            if (existingRecord) {
                              existingRecord.count++;
                              existingRecord.totalScore += result.percentage;
                              existingRecord.avgScore = Math.round(existingRecord.totalScore / existingRecord.count);
                            } else {
                              acc.push({
                                difficulty: result.difficulty,
                                count: 1,
                                totalScore: result.percentage,
                                avgScore: result.percentage
                              });
                            }
                            return acc;
                          }, [])}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="difficulty" />
                          <YAxis domain={[0, 100]} />
                          <Tooltip formatter={(value) => [`${value}%`]} />
                          <Legend />
                          <Bar dataKey="avgScore" name="Average Score" fill="#8884d8" />
                        </ReChartsBarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle>Difficulty Trends Over Time</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[350px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <ReChartsLineChart data={performanceTrend}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis domain={[0, 100]} />
                          <Tooltip formatter={(value) => [`${value}%`]} />
                          <Legend />
                          {['easy', 'medium', 'hard'].map(difficulty => (
                            <Line
                              key={difficulty}
                              type="monotone"
                              dataKey="percentage"
                              data={performanceTrend.filter(item => item.difficulty === difficulty)}
                              name={`${difficulty.charAt(0).toUpperCase() + difficulty.slice(1)} Questions`}
                              stroke={DIFFICULTY_COLORS[difficulty as 'easy' | 'medium' | 'hard']}
                              activeDot={{ r: 8 }}
                            />
                          ))}
                        </ReChartsLineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Time Impact Tab */}
            <TabsContent value="time">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Score vs. Quiz Frequency</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[350px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <ReChartsLineChart data={timePerformance.filter(item => item.hoursElapsed > 0)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                          <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" domain={[0, 'dataMax']} />
                          <Tooltip />
                          <Legend />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="score"
                            name="Score (%)"
                            stroke="#8884d8"
                            activeDot={{ r: 8 }}
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="hoursElapsed"
                            name="Hours Between Quizzes"
                            stroke="#82ca9d"
                          />
                        </ReChartsLineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Performance Calendar</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-lg border p-4">
                      <div className="grid grid-cols-7 gap-1">
                        {results.map((result, index) => {
                          const date = new Date(result.completed_at);
                          const day = date.getDate();
                          const formattedDate = format(date, 'yyyy-MM-dd');
                          
                          let bgColor = 'bg-red-200';
                          if (result.percentage >= 70) bgColor = 'bg-green-200';
                          else if (result.percentage >= 40) bgColor = 'bg-yellow-200';
                          
                          return (
                            <div 
                              key={index}
                              title={`${formattedDate}: ${result.percentage}%`}
                              className={`p-2 ${bgColor} text-center text-xs rounded-md hover:opacity-80 cursor-pointer`}
                              onClick={() => navigate(`/current-affairs/quiz-results/${result.id}`)}
                            >
                              {day}
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-4 flex items-center justify-center space-x-2 text-xs text-muted-foreground">
                        <div className="flex items-center">
                          <div className="mr-1 h-3 w-3 rounded-sm bg-red-200"></div>
                          <span>Below 40%</span>
                        </div>
                        <div className="flex items-center">
                          <div className="mr-1 h-3 w-3 rounded-sm bg-yellow-200"></div>
                          <span>40-70%</span>
                        </div>
                        <div className="flex items-center">
                          <div className="mr-1 h-3 w-3 rounded-sm bg-green-200"></div>
                          <span>Above 70%</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle>Quiz Completion Timeline</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="relative">
                      <div className="absolute left-9 top-0 bottom-0 w-px bg-border"></div>
                      <ul className="space-y-4">
                        {results.slice(0, 10).map((result, index) => (
                          <li key={index} className="flex items-start">
                            <div className="flex-shrink-0 w-9">
                              {result.percentage >= 70 ? (
                                <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                                  <Target className="h-4 w-4 text-green-500" />
                                </div>
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                                  <Clock className="h-4 w-4 text-amber-500" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4 -mt-1">
                              <div className="font-medium">
                                {format(new Date(result.completed_at), 'PPP')} at {format(new Date(result.completed_at), 'p')}
                              </div>
                              <div className="text-sm text-muted-foreground mt-1">
                                Scored {result.percentage}% on {result.difficulty} difficulty
                              </div>
                              <Button 
                                variant="link" 
                                className="p-0 h-auto text-sm mt-1 text-primary"
                                onClick={() => navigate(`/current-affairs/quiz-results/${result.id}`)}
                              >
                                View Details
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default CurrentAffairsAnalytics;
